package fr.frux.core.maps

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.sp
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

// Echantillons de la legende de la carte, dessines ici a partir des constantes des sprites plutot
// que recopies dans la feature : un reglage de taille, de bord ou d'orbite se reflete dans la
// legende sans double maintenance. Rendu Compose et non sprite MapLibre, mais meme geometrie.

/** Part du glyphe sur la plaque d'un marqueur d'arbre (20 dp sur 36). */
private const val MARKER_GLYPH_FRACTION = 20f / 36f

/**
 * Marqueur d'arbre tel que la carte le montre : bord blanc, plaque a la teinte de famille, glyphe
 * blanc. [scale] reduit l'ensemble sans toucher aux proportions.
 */
@Composable
fun TreeMarkerSample(
    glyph: ImageVector,
    tint: Color,
    modifier: Modifier = Modifier,
    scale: Float = 1f,
) {
    val outer = MARKER_BASE_SIZE * MARKER_RIM_SCALE * scale
    val plaque = MARKER_BASE_SIZE * scale
    Box(
        modifier = modifier.size(outer).background(MARKER_RIM_COLOR, CircleShape),
        contentAlignment = Alignment.Center,
    ) {
        Box(
            modifier = Modifier.size(plaque).background(tint, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                imageVector = glyph,
                contentDescription = null,
                tint = GLYPH_COLOR,
                modifier = Modifier.size(plaque * MARKER_GLYPH_FRACTION),
            )
        }
    }
}

/**
 * Regroupement tel que la carte le montre : pastille vert foret au compteur, couronne d'un
 * satellite par espece en partant du sommet dans le sens horaire (#710), au plus
 * [MAX_CLUSTER_SATELLITES]. Les [satellites] sont donnes du plus represente au moins represente.
 */
@Composable
fun ClusterSample(
    count: Int,
    satellites: List<Pair<ImageVector, Color>>,
    modifier: Modifier = Modifier,
    scale: Float = 1f,
) {
    val shown = satellites.take(MAX_CLUSTER_SATELLITES)
    val cluster = CLUSTER_SIZE * scale
    val satellite = SATELLITE_SIZE * scale
    val orbit = SATELLITE_ORBIT * scale
    Box(
        modifier = modifier.size(clusterSampleSize(scale)),
        contentAlignment = Alignment.Center,
    ) {
        shown.forEachIndexed { rank, (glyph, tint) ->
            val angle = -PI / 2 + rank * 2 * PI / shown.size
            SatelliteDot(
                glyph = glyph,
                tint = tint,
                size = satellite,
                rim = SATELLITE_RIM_WIDTH * scale,
                modifier = Modifier.offset(x = orbit * cos(angle).toFloat(), y = orbit * sin(angle).toFloat()),
            )
        }
        Box(
            modifier =
                Modifier
                    .size(cluster)
                    .background(CLUSTER_COLOR, CircleShape)
                    .border(CLUSTER_RIM_WIDTH * scale, MARKER_RIM_COLOR, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = count.toString(),
                style = TextStyle(color = CLUSTER_TEXT_COLOR, fontSize = 13.sp * scale, fontWeight = FontWeight.Medium),
            )
        }
    }
}

/** Emprise totale d'un [ClusterSample] : la pastille et sa couronne complete. */
fun clusterSampleSize(scale: Float = 1f): Dp = (SATELLITE_ORBIT * 2 + SATELLITE_SIZE) * scale

@Composable
private fun SatelliteDot(
    glyph: ImageVector,
    tint: Color,
    size: Dp,
    rim: Dp,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier.size(size).background(tint, CircleShape).border(rim, MARKER_RIM_COLOR, CircleShape),
        contentAlignment = Alignment.Center,
    ) {
        Icon(
            imageVector = glyph,
            contentDescription = null,
            tint = GLYPH_COLOR,
            modifier = Modifier.size((size - rim * 2) * SATELLITE_GLYPH_FRACTION),
        )
    }
}
